import type { Conversation } from '@twilio/conversations';

import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

type ChatStoreState = {
  supportConversation: Conversation | null;
  updatesConversation: Conversation | null;
  archivedConversations: Conversation[];
  supportUnreadCount: number;
  updatesUnreadCount: number;
  lockChat: boolean;
  inboxRefreshKey: number;
  setSupportConversation: (conversation: Conversation | null) => void;
  setUpdatesConversation: (conversation: Conversation | null) => void;
  setArchivedConversations: (conversations: Conversation[]) => void;
  setSupportUnreadCount: (count: number) => void;
  setUpdatesUnreadCount: (count: number) => void;
  setLockChat: (lockChat: boolean) => void;
  bumpInboxRefreshKey: () => void;
  resetChatState: () => void;
};

const initialState = {
  supportConversation: null,
  updatesConversation: null,
  archivedConversations: [],
  supportUnreadCount: 0,
  updatesUnreadCount: 0,
  lockChat: false,
  inboxRefreshKey: 0,
} satisfies Omit<
  ChatStoreState,
  | 'setSupportConversation'
  | 'setUpdatesConversation'
  | 'setArchivedConversations'
  | 'setSupportUnreadCount'
  | 'setUpdatesUnreadCount'
  | 'setLockChat'
  | 'bumpInboxRefreshKey'
  | 'resetChatState'
>;

const useChatStore = createWithEqualityFn<ChatStoreState>(
  (set) => ({
    ...initialState,
    setSupportConversation: (supportConversation) => set({ supportConversation }),
    setUpdatesConversation: (updatesConversation) => set({ updatesConversation }),
    setArchivedConversations: (archivedConversations) =>
      set({ archivedConversations }),
    setSupportUnreadCount: (supportUnreadCount) => set({ supportUnreadCount }),
    setUpdatesUnreadCount: (updatesUnreadCount) => set({ updatesUnreadCount }),
    setLockChat: (lockChat) =>
      set({ lockChat }),
    bumpInboxRefreshKey: () =>
      set((state) => ({ inboxRefreshKey: state.inboxRefreshKey + 1 })),
    resetChatState: () => set(initialState),
  }),
  shallow,
);

const useTotalUnreadCount = () =>
  useChatStore((state) => state.supportUnreadCount + state.updatesUnreadCount);

export { useChatStore, useTotalUnreadCount };
export type { ChatStoreState };
